import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  fileExtensionKey,
  formatAttachmentSize,
  formatFileExtensionLabel,
  sharePointProofVariantLabel,
  uniqueFileExtensions,
} from "@/lib/mailAttachments";

export interface SharePointMailAttachmentItem {
  id?: string | null;
  name?: string | null;
  size?: number | null;
  project_hint?: string | null;
}

interface SharePointMailAttachmentListProps {
  items: SharePointMailAttachmentItem[];
  selectedIds: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  listClassName?: string;
}

export function SharePointMailAttachmentList({
  items,
  selectedIds,
  onToggle,
  listClassName = "max-h-52",
}: SharePointMailAttachmentListProps) {
  const [activeExt, setActiveExt] = useState<string | null>(null);

  const extensions = useMemo(
    () => uniqueFileExtensions(items.map((it) => it.name)),
    [items],
  );

  useEffect(() => {
    if (activeExt !== null && !extensions.includes(activeExt)) {
      setActiveExt(null);
    }
  }, [activeExt, extensions]);

  const visibleItems = useMemo(() => {
    if (activeExt === null) return items;
    return items.filter((it) => fileExtensionKey(it.name) === activeExt);
  }, [items, activeExt]);

  return (
    <div>
      {extensions.length > 1 ? (
        <div className="mb-2 flex flex-wrap gap-1.5" role="group" aria-label="Nach Dateiformat filtern">
          <FormatChip
            label="Alle"
            active={activeExt === null}
            onClick={() => setActiveExt(null)}
          />
          {extensions.map((ext) => (
            <FormatChip
              key={ext || "none"}
              label={formatFileExtensionLabel(ext)}
              active={activeExt === ext}
              onClick={() => setActiveExt(ext)}
            />
          ))}
        </div>
      ) : null}
      <ul className={clsx("space-y-1 overflow-y-auto text-sm", listClassName)}>
        {visibleItems.map((it) => {
          const sid = String(it.id || "").trim();
          if (!sid) return null;
          const size = formatAttachmentSize(Number(it.size) || 0);
          const title = [
            it.name,
            it.project_hint ? ` (${it.project_hint})` : "",
            size ? ` · ${size}` : "",
          ]
            .filter(Boolean)
            .join("");
          const variant = sharePointProofVariantLabel(String(it.name || ""));
          return (
            <li key={sid}>
              <label className="flex cursor-pointer gap-2 rounded-md px-2 py-1 hover:bg-fill-tertiary">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selectedIds.has(sid)}
                  onChange={(e) => onToggle(sid, e.target.checked)}
                />
                <span className="min-w-0">
                  <span className="break-all">{title}</span>
                  {variant ? (
                    <span className="block text-xs text-label-tertiary">{variant}</span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FormatChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "rounded-full px-2.5 py-1 text-xs font-semibold transition",
        active
          ? "bg-accent text-label-on-accent"
          : "bg-card text-label-secondary ring-1 ring-inset ring-separator hover:bg-fill-tertiary",
      )}
    >
      {label}
    </button>
  );
}
